import {
  useState,
  useRef,
  useEffect,
  useMemo,
  type KeyboardEvent,
} from 'react';

// Search modal should not have the 'X' closing icon, so we use Dialog from MUI directly here instead of our wrapper Dialog component
// eslint-disable-next-line no-restricted-imports
import { Alert, Dialog, Stack, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Trans, useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

import { CmdKTag } from './components/CmdKTag';
import { SearchResultItem } from './components/SearchResultItem';
import { SearchResultShimmer } from './components/SearchResultShimmer';
import styles from './SearchModal.module.scss';
import { useGetTypeSenseAPIKeys } from '@/api/REST/typeSenseAPIKeys';
import {
  useGetAllActions,
  useQuickResultSearch,
  useGoToResultSearch,
  type FormattedSearchResult,
} from '@/api/typesense/search';
import { Icon } from '@/components/ui/Icon';
import { uiIcons } from '@/constants/uiIcons';
import { useSearchStore } from '@/stores/search';
import {
  RESULT_LIST_ID,
  INPUT_ID,
  RESULT_ITEM_ID_PREFIX,
  useSearchNavigationStore,
} from '@/stores/searchNavigation';

export const SearchModal = () => {
  const { isMainSearchOpen, updateStore } = useSearchStore();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearchInput] = useDebounce(searchInput, 200);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const [hasSearchError, setHasSearchError] = useState(false);

  const {
    activeIndex,
    setTotalResults,
    moveNext,
    movePrev,
    moveToIndex,
    reset: resetNavigation,
  } = useSearchNavigationStore();

  const { t } = useTranslation('searchModal');

  const {
    isError: isTypesenseApiKeysError,
    isLoading: isTypesenseApiKeysLoading,
    data: typesenseApiKeys,
  } = useGetTypeSenseAPIKeys();

  const toggleModal = () => {
    updateStore({
      type: 'TOGGLE_SEARCH',
    });
    setSearchInput('');
    resetNavigation();
    setHasSearchError(false);
  };

  const handleDialogEntered = () => {
    // Focus input after dialog animation completes
    searchInputRef.current?.focus();
  };

  const {
    data: initialActions,
    isLoading: isLoadingInitialActions,
    error: initialActionsError,
    hasActionsRights,
  } = useGetAllActions(isMainSearchOpen);

  const {
    searchResult: quickSearchResult,
    isSearchResultEmpty: isQuickSearchResultEmpty,
    isLoading: isQuickSearchLoading,
    status: quickSearchStatus,
    fetchStatus: quickSearchFetchStatus,
  } = useGoToResultSearch(debouncedSearchInput, 5);

  const {
    searchResult: resultSearchResult,
    isSearchResultEmpty: isResultSearchResultEmpty,
    isLoading: isResultSearchLoading,
    status: resultSearchStatus,
    fetchStatus: resultSearchFetchStatus,
  } = useQuickResultSearch(debouncedSearchInput, 30);

  // Sticky error handling: only clear on fresh successful results
  useEffect(() => {
    // Clear error when search is empty
    if (!debouncedSearchInput) {
      setHasSearchError(false);
      return;
    }

    // Set error when either search fails
    if (quickSearchStatus === 'error' || resultSearchStatus === 'error') {
      setHasSearchError(true);
      return;
    }

    // Clear error only when we get a fresh successful result
    // (both queries succeeded and are not fetching)
    if (
      quickSearchStatus === 'success' &&
      resultSearchStatus === 'success' &&
      quickSearchFetchStatus === 'idle' &&
      resultSearchFetchStatus === 'idle'
    ) {
      setHasSearchError(false);
    }
  }, [
    debouncedSearchInput,
    quickSearchStatus,
    resultSearchStatus,
    quickSearchFetchStatus,
    resultSearchFetchStatus,
  ]);

  const handleResultSelect = (
    _item: FormattedSearchResult,
    shouldCloseModal: boolean,
  ) => {
    if (shouldCloseModal) {
      setSearchInput('');

      updateStore({
        type: 'RESET_SEARCH',
      });
    }
  };

  const displaySearchResultsList = useMemo(
    () =>
      !(
        isQuickSearchLoading ||
        isResultSearchLoading ||
        !debouncedSearchInput ||
        debouncedSearchInput.trim().length === 0 ||
        (isQuickSearchResultEmpty && isResultSearchResultEmpty)
      ),
    [
      isQuickSearchLoading,
      isResultSearchLoading,
      debouncedSearchInput,
      isQuickSearchResultEmpty,
      isResultSearchResultEmpty,
    ],
  );

  // Get all available results in the correct order
  const allResults = useMemo(
    () =>
      displaySearchResultsList
        ? [...(quickSearchResult || []), ...(resultSearchResult || [])]
        : initialActions || [],
    [
      displaySearchResultsList,
      quickSearchResult,
      resultSearchResult,
      initialActions,
    ],
  );

  const totalResults = useMemo(() => allResults.length, [allResults]);

  // Number of items to jump when using Page Up/Down
  // Based on ~400px container height and ~60-70px item height
  const PAGE_SIZE = 5;

  // Update total results in store when they change
  useEffect(() => {
    setTotalResults(totalResults);
  }, [totalResults, setTotalResults]);

  // Reset active index and scroll to top when results change
  useEffect(() => {
    resetNavigation();
    // Scroll results list back to top when search input changes
    if (resultsListRef.current) {
      resultsListRef.current.scrollTop = 0;
    }
  }, [debouncedSearchInput, displaySearchResultsList, resetNavigation]);

  // Handle keyboard navigation - attached to root container for event delegation
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (totalResults === 0) return;

    // Check IME composition
    if (e.nativeEvent.isComposing) return;

    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          movePrev();
        } else {
          moveNext();
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveNext();
        break;
      case 'ArrowUp':
        e.preventDefault();
        movePrev();
        break;
      case 'Home':
        // Only handle Home when navigating results, otherwise allow native behavior
        if (activeIndex >= 0) {
          e.preventDefault();
          moveToIndex(0);
        }
        break;
      case 'End':
        // Only handle End when navigating results, otherwise allow native behavior
        if (activeIndex >= 0) {
          e.preventDefault();
          moveToIndex(totalResults - 1);
        }
        break;
      case 'PageUp':
        // Only handle PageUp when navigating results, otherwise allow native behavior
        if (activeIndex >= 0) {
          e.preventDefault();
          moveToIndex(Math.max(0, activeIndex - PAGE_SIZE));
        }
        break;
      case 'PageDown':
        // Only handle PageDown when navigating results, otherwise allow native behavior
        if (activeIndex >= 0) {
          e.preventDefault();
          moveToIndex(Math.min(totalResults - 1, activeIndex + PAGE_SIZE));
        }
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < totalResults) {
          e.preventDefault();
          const selectedResult = allResults[activeIndex];
          if (selectedResult) {
            // Check if Ctrl/Cmd key is pressed for opening in new tab
            if (e.ctrlKey || e.metaKey) {
              // Open in new tab
              window.open(
                selectedResult.pageUrl,
                '_blank',
                'noopener,noreferrer',
              );
            } else {
              // Normal navigation
              const linkElement = document.getElementById(
                `${RESULT_ITEM_ID_PREFIX}${activeIndex}`,
              );
              if (linkElement) {
                linkElement.click();
              }
            }
          }
        }
        break;
    }
  };

  return (
    <Dialog
      className={styles.SearchModal}
      open={isMainSearchOpen}
      onClose={toggleModal}
      sx={{ '.MuiPaper-root': { maxWidth: '900px' } }}
      slotProps={{
        transition: {
          onEntered: handleDialogEntered,
        },
      }}
    >
      <div className={styles.SearchModalContent} onKeyDown={handleKeyDown}>
        <div className={styles.SearchInputWrapper}>
          <label htmlFor={INPUT_ID} style={{ ...visuallyHidden }}>
            {t('SearchInputPlaceholderLabel')}
          </label>
          <input
            className={styles.SearchInput}
            type="text"
            id={INPUT_ID}
            ref={searchInputRef}
            placeholder={
              hasActionsRights
                ? t('SearchInputPlaceholderLabel')
                : t('NoRightsPlaceholderLabel')
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            role="combobox"
            aria-expanded={totalResults > 0}
            aria-controls={RESULT_LIST_ID}
            aria-activedescendant={
              activeIndex >= 0
                ? `${RESULT_ITEM_ID_PREFIX}${activeIndex}`
                : undefined
            }
            aria-describedby="search-input-description"
            aria-autocomplete="list"
            autoComplete="off"
            autoCorrect="off"
          />

          {!displaySearchResultsList ? <CmdKTag /> : null}
        </div>

        <div
          className={styles.ResultList}
          id={RESULT_LIST_ID}
          role="listbox"
          aria-label={t('ResultGroupHeading')}
          ref={resultsListRef}
        >
          {!displaySearchResultsList ? (
            isQuickSearchLoading ||
            isResultSearchLoading ||
            isLoadingInitialActions ||
            isTypesenseApiKeysLoading ? (
              <SearchResultShimmer
                aria-label={t('LoadingState')}
                aria-live="polite"
              />
            ) : (
              <>
                {isTypesenseApiKeysError ||
                (!typesenseApiKeys &&
                  !isTypesenseApiKeysLoading &&
                  !isLoadingInitialActions) ||
                (hasSearchError && debouncedSearchInput) ? (
                  <Alert severity="error" sx={{ boxShadow: 'none' }}>
                    {t('SearchUnavailableMessage')}
                  </Alert>
                ) : initialActionsError ? (
                  <Alert severity="error" sx={{ boxShadow: 'none' }}>
                    {t('ErrorMessage')}
                  </Alert>
                ) : null}
                {hasActionsRights && !hasSearchError && (
                  <Stack gap={2}>
                    {debouncedSearchInput.trim().length > 0 ? (
                      <Stack direction="row" alignItems="center" gap={1} pl={1}>
                        <Icon name={uiIcons.status.error} />
                        {t('NoResultsFound')}
                      </Stack>
                    ) : null}
                    <Stack gap={1}>
                      <Typography variant="label">
                        {t('CollectionActionsLabel')}
                      </Typography>
                      <Stack gap={0.5}>
                        {initialActions?.map((item, index) => (
                          <SearchResultItem
                            key={`initial-${item.raw?.document?.id || index}`}
                            result={item}
                            resultId={`${RESULT_ITEM_ID_PREFIX}${index}`}
                            isActive={activeIndex === index}
                            onItemSelect={handleResultSelect}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </>
            )
          ) : (
            <Stack gap={3}>
              <Stack gap={1}>
                <Typography variant="label">{t('GoToHeading')}</Typography>
                <Stack gap={0.5}>
                  {isQuickSearchResultEmpty ? (
                    <Typography color="text.secondary" pl={1}>
                      {t('NoResultsFound')}
                    </Typography>
                  ) : (
                    quickSearchResult?.map((result, index) => {
                      const globalIndex = index;
                      return (
                        <SearchResultItem
                          key={`quick-${index}`}
                          result={result}
                          resultId={`${RESULT_ITEM_ID_PREFIX}${globalIndex}`}
                          isActive={activeIndex === globalIndex}
                          onItemSelect={handleResultSelect}
                        />
                      );
                    })
                  )}
                </Stack>
              </Stack>
              <Stack gap={1}>
                <Typography variant="label">
                  {t('QuickResultsHeading')}
                </Typography>
                <Stack gap={0.5}>
                  {isResultSearchResultEmpty ? (
                    <Typography color="text.secondary" pl={1}>
                      {t('NoResultsFound')}
                    </Typography>
                  ) : (
                    resultSearchResult?.map((result, index) => {
                      const globalIndex =
                        (quickSearchResult?.length || 0) + index;
                      return (
                        <SearchResultItem
                          key={`result-${index}`}
                          result={result}
                          resultId={`${RESULT_ITEM_ID_PREFIX}${globalIndex}`}
                          isActive={activeIndex === globalIndex}
                          onItemSelect={handleResultSelect}
                        />
                      );
                    })
                  )}
                </Stack>
              </Stack>
            </Stack>
          )}
        </div>
        <Stack direction="row" alignItems="center" pt={1}>
          <Icon name={uiIcons.content.tip} />
          <Typography id="search-input-description" variant="label">
            <Trans
              i18nKey="KeyboardNavigationTip"
              ns="searchModal"
              components={{
                1: <strong />,
                2: <strong />,
                3: <strong />,
                4: <strong />,
              }}
            />
          </Typography>
        </Stack>
      </div>
    </Dialog>
  );
};
